import {Material, MaterialType} from 'components/customer/Materials/entity';
import {Batch, OperationGroup} from 'components/customer/Preview3D/types';
import {store} from 'store/customer';
import {getDoor} from 'components/customer/Materials/store/selectors/doorSelector';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';

// Result from shaker door detection
export interface ShakerDoorInfo {
    isShakerDoor: boolean;
}

// Check if we should use shaker doors based on material and door selection
// door parameter is optional - if not provided, will read from store (less efficient)
export const detectShakerDoor = (
    material: Material | null | undefined,
    door?: ReturnType<typeof getDoor> | null,
    isAdvancedDoor?: boolean
): ShakerDoorInfo => {
    // If door not provided, read from store (for backwards compatibility)
    // Note: This is less efficient as it bypasses React-Redux subscriptions
    const resolvedDoor =
        door !== undefined
            ? door
            : getDoor(store.getState(), MaterialType.EXTERIOR, 0);

    const legacyAdvanced = (
        resolvedDoor as {advanced?: boolean | number} | null | undefined
    )?.advanced;

    const isAdvanced = Boolean(
        resolvedDoor?.is_advanced ||
        legacyAdvanced === true ||
        legacyAdvanced == 1 ||
        isAdvancedDoor
    );
    return {isShakerDoor: isAdvanced};
};

const collectOperationGroups = (batch: Batch): OperationGroup[] => {
    const collected: OperationGroup[] = [];

    const jobs = (batch as {Jobs?: unknown} | null)?.Jobs;
    if (!jobs || typeof jobs !== 'object') return collected;

    for (const job of Object.values(jobs as Record<string, unknown>)) {
        const manufacturingMap = (job as {Manufacturing?: unknown} | null)
            ?.Manufacturing;
        if (!manufacturingMap || typeof manufacturingMap !== 'object') continue;

        for (const manufacturing of Object.values(
            manufacturingMap as Record<string, unknown>
        )) {
            const groups = (manufacturing as {OperationGroups?: unknown} | null)
                ?.OperationGroups;
            if (!groups || typeof groups !== 'object') continue;

            for (const group of Object.values(
                groups as Record<string, unknown>
            )) {
                if (group && typeof group === 'object') {
                    collected.push(group as OperationGroup);
                }
            }
        }
    }

    return collected;
};

const SPLIT_DOOR_PART_SUFFIXES = ['DoorTop', 'DoorBottom'];

export const isSplitDoorOperationGroup = (
    operationGroup: OperationGroup
): boolean =>
    Boolean(operationGroup.IsLeftDoor || operationGroup.IsRightDoor) &&
    SPLIT_DOOR_PART_SUFFIXES.some((suffix) =>
        Boolean(operationGroup.PartID?.endsWith(suffix))
    );

export const hasSplitDoors = (operationGroups: OperationGroup[]): boolean => {
    const halves = operationGroups.filter(isSplitDoorOperationGroup);
    return (
        halves.filter((group) => group.IsLeftDoor).length > 1 ||
        halves.filter((group) => group.IsRightDoor).length > 1
    );
};

export const getExistingDoorsFromTemplate = (batch: Batch) => {
    const groups = collectOperationGroups(batch);
    const countOf = (flag: (group: OperationGroup) => unknown): number =>
        groups.filter((group) => Boolean(flag(group))).length;

    const hasLeftDoor = countOf((g) => g.IsLeftDoor) > 0;
    const hasRightDoor = countOf((g) => g.IsRightDoor) > 0;
    const hasCornerLeftDoor = countOf((g) => g.IsCornerLeftDoor) > 0;
    const hasCornerRightDoor = countOf((g) => g.IsCornerRightDoor) > 0;
    const hasTopHangDoor = countOf((g) => g.IsTopHangDoor) > 0;
    const hasLeftApplianceMainDoor =
        countOf((g) => g.IsLeftApplianceMainDoor) > 0;
    const hasLeftApplianceExtendedDoor =
        countOf((g) => g.IsLeftApplianceExtendedDoor) > 0;
    const hasRightApplianceMainDoor =
        countOf((g) => g.IsRightApplianceMainDoor) > 0;
    const hasRightApplianceExtendedDoor =
        countOf((g) => g.IsRightApplianceExtendedDoor) > 0;

    return {
        hasLeftDoor,
        hasRightDoor,
        hasAnyDoors:
            hasLeftDoor ||
            hasRightDoor ||
            hasCornerRightDoor ||
            hasCornerLeftDoor ||
            hasTopHangDoor ||
            hasLeftApplianceMainDoor ||
            hasLeftApplianceExtendedDoor ||
            hasRightApplianceExtendedDoor ||
            hasRightApplianceMainDoor,
        isPairDoor: hasLeftDoor && hasRightDoor,
        isCornerPairDoor: hasCornerRightDoor && hasCornerLeftDoor,
        hasCornerRightDoor,
        hasCornerLeftDoor,
        hasTopHangDoor,
        hasLeftApplianceMainDoor,
        hasLeftApplianceExtendedDoor,
        hasRightApplianceMainDoor,
        hasRightApplianceExtendedDoor,
        hasBiFoldLeftDoor: countOf((g) => g.IsBifoldLeftDoor) > 0,
        hasBiFoldRightDoor: countOf((g) => g.IsBifoldRightDoor) > 0,
        hasDoorPullout: countOf((g) => g.IsDoorPullout) > 0,
        hasCorFoldKitLeftDoor: countOf((g) => g.IsCorFoldKitLeftDoor) > 0,
        hasCorFoldKitRightDoor: countOf((g) => g.IsCorFoldKitRightDoor) > 0,
        hasSplitDoor: hasSplitDoors(groups),
    };
};

export const hasPairDoors = (batch: Batch): boolean => {
    const {hasLeftDoor, hasRightDoor} = getExistingDoorsFromTemplate(batch);
    return hasLeftDoor && hasRightDoor;
};

export const isCorFoldKitDoor = (operationGroup: OperationGroup): boolean =>
    Boolean(
        operationGroup.IsCorFoldKitLeftDoor ||
        operationGroup.IsCorFoldKitRightDoor
    );

export const isShakerFoldKitDoor = (
    mesh: {name: string},
    operationGroup: OperationGroup
): boolean => mesh.name === 'ShakerDoors' && isCorFoldKitDoor(operationGroup);

export const isDoorStylePanelOperationGroup = (
    operationGroup: OperationGroup,
    variables: TemplateVariable | undefined
): boolean =>
    Boolean(variables?.isDoorStylePanel) &&
    operationGroup.ExteriorMaterial?.toString() === 'true';

export const buildsShakerPerOperationGroup = (
    operationGroup: OperationGroup,
    {
        isBiFoldDoor,
        isTopHang,
        isSplitDoor,
    }: {isBiFoldDoor: boolean; isTopHang: boolean; isSplitDoor: boolean}
): boolean =>
    isBiFoldDoor ||
    isTopHang ||
    isSplitDoor ||
    isCorFoldKitDoor(operationGroup);

export const isDoorOperationGroup = (operationGroup: OperationGroup) => {
    return (
        operationGroup.IsLeftDoor ||
        operationGroup.IsRightDoor ||
        operationGroup.IsPairDoor ||
        operationGroup.IsCornerRightDoor ||
        operationGroup.IsCornerLeftDoor ||
        operationGroup.IsDrawerFront ||
        operationGroup.IsLeftApplianceExtendedDoor ||
        operationGroup.IsLeftApplianceMainDoor ||
        operationGroup.IsRightApplianceExtendedDoor ||
        operationGroup.IsRightApplianceMainDoor ||
        operationGroup.IsBifoldLeftDoor ||
        operationGroup.IsBifoldRightDoor ||
        operationGroup.IsLeftBifoldExtension ||
        operationGroup.IsTopHangDoor ||
        operationGroup.IsDoorPullout ||
        operationGroup.IsCorFoldKitLeftDoor ||
        operationGroup.IsCorFoldKitRightDoor
    );
};
